import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { individualTaskApiToIndividualTaskEntity } from '../../entities/IndividualTask/converters';
import { pagingMetadataToCursorResponse } from '../../utils/PagingUtils';
import { BaseClient } from '../BaseClient';
export class IndividualTaskClientBase extends BaseClient {
  constructor() {
    super(...arguments);
    _defineProperty(this, "getTaskImpl", void 0);
    _defineProperty(this, "queryTasksImpl", void 0);
    _defineProperty(this, "createTaskImpl", void 0);
    _defineProperty(this, "bulkCreateTasksImpl", void 0);
    _defineProperty(this, "updateTaskImpl", void 0);
    _defineProperty(this, "appendTaskLogsImpl", void 0);
  }
  async getTask(taskId) {
    try {
      for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }
      const {
        task
      } = await this.request(this.getTaskImpl({
        taskId
      }), ...args);
      if (!task) {
        throw new Error('Task not found');
      }
      return this.convertToEntity(task);
    } catch (error) {
      this.logError('getTask', 'Failed to get task', {
        taskId
      }, error);
      throw error;
    }
  }
  async queryTasks(query) {
    try {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      const {
        tasks,
        pagingMetadata
      } = await this.request(this.queryTasksImpl({
        query
      }), ...args);
      if (!tasks) {
        throw new Error('tasks is not an array');
      }
      return {
        items: tasks.map(task => this.convertToEntity(task)),
        ...pagingMetadataToCursorResponse(pagingMetadata)
      };
    } catch (error) {
      this.logError('queryTasks', 'Failed to query tasks', {
        query
      }, error);
      throw error;
    }
  }
  async createTask(task) {
    try {
      for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        args[_key3 - 1] = arguments[_key3];
      }
      const {
        task: createdTask
      } = await this.request(this.createTaskImpl({
        task
      }), ...args);
      if (!createdTask) {
        throw new Error('Failed to create task');
      }
      return this.convertToEntity(createdTask);
    } catch (error) {
      this.logError('createTask', 'Failed to create task', {
        task
      }, error);
      throw error;
    }
  }
  async bulkCreateTasks(tasks, returnEntity) {
    if (returnEntity === void 0) {
      returnEntity = false;
    }
    try {
      var _response$bulkOperati, _response$bulkOperati2;
      for (var _len4 = arguments.length, args = new Array(_len4 > 2 ? _len4 - 2 : 0), _key4 = 2; _key4 < _len4; _key4++) {
        args[_key4 - 2] = arguments[_key4];
      }
      const response = await this.request(this.bulkCreateTasksImpl({
        tasks,
        returnEntity
      }), ...args);
      return {
        results: (response.results ?? []).map(r => {
          var _r$itemMetadata, _r$itemMetadata2, _r$itemMetadata3, _r$itemMetadata4, _r$itemMetadata5;
          return {
            originalIndex: ((_r$itemMetadata = r.itemMetadata) == null ? void 0 : _r$itemMetadata.originalIndex) ?? 0,
            success: ((_r$itemMetadata2 = r.itemMetadata) == null ? void 0 : _r$itemMetadata2.success) ?? false,
            taskId: ((_r$itemMetadata3 = r.itemMetadata) == null ? void 0 : _r$itemMetadata3.id) ?? undefined,
            errorCode: ((_r$itemMetadata4 = r.itemMetadata) == null || (_r$itemMetadata4 = _r$itemMetadata4.error) == null ? void 0 : _r$itemMetadata4.code) ?? undefined,
            errorMessage: ((_r$itemMetadata5 = r.itemMetadata) == null || (_r$itemMetadata5 = _r$itemMetadata5.error) == null ? void 0 : _r$itemMetadata5.description) ?? undefined,
            task: r.task ? this.convertToEntity(r.task) : undefined
          };
        }),
        totalSuccesses: ((_response$bulkOperati = response.bulkOperationMetadata) == null ? void 0 : _response$bulkOperati.totalSuccesses) ?? 0,
        totalFailures: ((_response$bulkOperati2 = response.bulkOperationMetadata) == null ? void 0 : _response$bulkOperati2.totalFailures) ?? 0
      };
    } catch (error) {
      this.logError('bulkCreateTasks', 'Failed to bulk-create tasks', {
        count: tasks.length
      }, error);
      throw error;
    }
  }
  async addTaskNotes(taskId, notes) {
    try {
      for (var _len5 = arguments.length, args = new Array(_len5 > 2 ? _len5 - 2 : 0), _key5 = 2; _key5 < _len5; _key5++) {
        args[_key5 - 2] = arguments[_key5];
      }
      const task = await this.getTask(taskId, ...args);
      const newNotes = [...(task.notes || []), ...notes];
      const {
        task: updatedTask
      } = await this.request(this.updateTaskImpl({
        task: {
          ...task,
          notes: newNotes
        },
        fieldMask: ['notes']
      }), ...args);
      if (!updatedTask) {
        throw new Error('Failed to update task notes');
      }
      return this.convertToEntity(updatedTask);
    } catch (error) {
      this.logError('updateTaskNotes', 'Failed to update task notes', {
        taskId,
        notes
      }, error);
      throw error;
    }
  }
  async addTaskLogs(taskId, logs) {
    try {
      for (var _len6 = arguments.length, args = new Array(_len6 > 2 ? _len6 - 2 : 0), _key6 = 2; _key6 < _len6; _key6++) {
        args[_key6 - 2] = arguments[_key6];
      }
      const task = await this.getTask(taskId, ...args);
      const newLogs = [...(task.logs || []), ...logs];
      const {
        task: updatedTask
      } = await this.request(this.updateTaskImpl({
        task: {
          ...task,
          logs: newLogs
        },
        fieldMask: ['logs']
      }), ...args);
      if (!updatedTask) {
        throw new Error('Failed to update task logs');
      }
      return this.convertToEntity(updatedTask);
    } catch (error) {
      this.logError('updateTaskLogs', 'Failed to update task logs', {
        taskId,
        logs
      }, error);
      throw error;
    }
  }
  async appendTaskLog(taskId, type, message) {
    try {
      for (var _len7 = arguments.length, args = new Array(_len7 > 3 ? _len7 - 3 : 0), _key7 = 3; _key7 < _len7; _key7++) {
        args[_key7 - 3] = arguments[_key7];
      }
      await this.request(this.appendTaskLogsImpl({
        taskId,
        type,
        message
      }), ...args);
    } catch (error) {
      this.logError('appendTaskLog', 'Failed to append task log', {
        taskId,
        type,
        message
      }, error);
      throw error;
    }
  }
  async updateTaskStatus(taskId, status, manuallyCompleted) {
    try {
      for (var _len8 = arguments.length, args = new Array(_len8 > 3 ? _len8 - 3 : 0), _key8 = 3; _key8 < _len8; _key8++) {
        args[_key8 - 3] = arguments[_key8];
      }
      const task = await this.getTask(taskId, ...args);
      const {
        task: updatedTask
      } = await this.request(this.updateTaskImpl({
        task: {
          ...task,
          status,
          manuallyCompleted
        },
        fieldMask: ['status', 'manuallyCompleted']
      }), ...args);
      if (!updatedTask) {
        throw new Error('Failed to update task status');
      }
      return this.convertToEntity(updatedTask);
    } catch (error) {
      this.logError('updateTaskStatus', 'Failed to update task status', {
        taskId,
        status,
        manuallyCompleted
      }, error);
      throw error;
    }
  }
  async updateTask(taskId, patch) {
    try {
      for (var _len9 = arguments.length, args = new Array(_len9 > 2 ? _len9 - 2 : 0), _key9 = 2; _key9 < _len9; _key9++) {
        args[_key9 - 2] = arguments[_key9];
      }
      const task = await this.getTask(taskId, ...args);
      const fieldMask = [];
      const next = {
        ...task
      };
      if (patch.status !== undefined && patch.status !== task.status) {
        next.status = patch.status;
        fieldMask.push('status');
      }
      if (patch.display !== undefined) {
        next.display = {
          ...(task.display ?? {}),
          ...patch.display
        };
        fieldMask.push('display');
      }
      if (fieldMask.length === 0) {
        return task;
      }
      const {
        task: updatedTask
      } = await this.request(this.updateTaskImpl({
        task: next,
        fieldMask
      }), ...args);
      if (!updatedTask) {
        throw new Error('Failed to update task');
      }
      return this.convertToEntity(updatedTask);
    } catch (error) {
      this.logError('updateTask', 'Failed', {
        taskId,
        patchKeys: Object.keys(patch)
      }, error);
      throw error;
    }
  }
  convertToEntity(taskApi) {
    return individualTaskApiToIndividualTaskEntity(taskApi);
  }
  getServiceNameForLogs() {
    return 'IndividualTaskClient';
  }
}
//# sourceMappingURL=IndividualTaskClientBase.js.map